import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CourseManagementService } from '../services/course.service';
import { IGetCodes, IGetCodesAvailableFeature } from 'src/app/interfaces/codes';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ToastrService } from 'ngx-toastr';

interface ICourseFeature extends IGetCodesAvailableFeature {
  isNew: boolean;
  isSelected: boolean;
}

export interface IAvailableFeature {
  Course_Id: number;
  Course_Feature_Type_Code: string;
  Course_Feature_Type_Name: string;
  Display_Order: number;
  New_Feature_Ind: string;
  Created_By: string;
  Creation_Date: string;
  Updated_By: string;
  Updation_Date: string;
}

@Component({
  selector: 'app-course-feature-availabillity-modal',
  templateUrl: './course-feature-availabillity-modal.component.html',
  styleUrls: ['./course-feature-availabillity-modal.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class CourseFeatureAvailabillityModalComponent implements OnInit {
  @Input() courseId: number;
  selectedFeatures: ICourseFeature[] = [];
  unselectedFeatures: ICourseFeature[] = [];
  availableFeatures: IAvailableFeature[] = [];

  constructor(
    public activeModal: NgbActiveModal,
    private readonly courseManagementService: CourseManagementService,
    private readonly toaster: ToastrService
  ) {}

  ngOnInit(): void {
    const payload = { CourseId: this.courseId };
    this.courseManagementService.getFeaturesForCoursePlanAvailable(payload).subscribe((ele) => {
      this.availableFeatures = ele;
      this.getAvailableFeatures();
    });
  }

  getAvailableFeatures() {
    this.courseManagementService
      .getFeaturesForCoursePlan(`AVAILABLE_COURSE_FEATURE@${this.courseId}`)
      .subscribe((res: IGetCodes[]) => {
        const mapped = res.map((feature) => {
          const matched = this.availableFeatures?.find(
            (e) => e.Course_Feature_Type_Code === feature.Code
          );
          return {
            ...feature,
            isSelected: !!matched,
            isNew: matched?.New_Feature_Ind === 'Y',
          };
        });

        const sorted = mapped.sort((a, b) => {
          if (a.isSelected !== b.isSelected) {
            return Number(b.isSelected) - Number(a.isSelected);
          }
          return a.Display_Order - b.Display_Order;
        });

        this.selectedFeatures = sorted.filter(f => f.isSelected);
        this.unselectedFeatures = sorted.filter(f => !f.isSelected);
      });
  }

  toggleFeatureSelection(feature: ICourseFeature) {
    feature.isSelected = !feature.isSelected;

    if (feature.isSelected) {
      // Get highest Display_Order from selectedFeatures only
      const maxSelectedOrder = this.selectedFeatures.reduce(
        (max, f) => Math.max(max, f.Display_Order || 0),
        0
      );
      feature.Display_Order = maxSelectedOrder + 1;

      this.unselectedFeatures = this.unselectedFeatures.filter(f => f.Code !== feature.Code);
      this.selectedFeatures.push(feature);
    } else {
      this.selectedFeatures = this.selectedFeatures.filter(f => f.Code !== feature.Code);
      feature.Display_Order = null;
      this.unselectedFeatures.unshift(feature);
    }

    // Sort only selectedFeatures
    this.selectedFeatures.sort((a, b) => (a.Display_Order ?? 0) - (b.Display_Order ?? 0));

    // Leave unselectedFeatures as-is to preserve original order
  }





  drop(event: CdkDragDrop<ICourseFeature[]>) {
    moveItemInArray(this.selectedFeatures, event.previousIndex, event.currentIndex);
    this.selectedFeatures.forEach((feature, index) => {
      feature.Display_Order = index + 1;
    });
  }

  save() {
    const allFeatures = [...this.selectedFeatures, ...this.unselectedFeatures];
    const payload = {
      CourseId: this.courseId,
      UDTAvailableCourseFeature: allFeatures
        .filter(feature => feature.isSelected)
        .map(feature => ({
          CourseFeatureTypeCode: feature.Code,
          DisplayOrder: feature.Display_Order,
          NewFeatureInd: feature.isNew ? 'Y' : 'N',
        })),
    };

    this.courseManagementService.insertOrUpdateAvailableCourseFeature(payload).subscribe({
      next: () => {
        this.activeModal.close();
        this.toaster.success('Saved Successfully.', 'Success', { timeOut: 500 });
      },
      error: (err) => {
        console.error('API Error', err);
      }
    });
  }
}
